<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import { storeToRefs } from "pinia";

import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxLabel,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/vue";
import { ChevronDownIcon } from "@heroicons/vue/20/solid";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/vue/24/outline";

import date from "@/libs/date.ts";
import { useDateRange } from "@/components/DateRange/state.ts";

type WeekOption = { value: string; label: string };

const state = useDateRange();

const emit = defineEmits(["refresh"]);

const { weeks, week, from, to } = storeToRefs(state);

// Selecting a week outside the loaded window shifts it, so ask for fresh transactions.
watch([from, to], () => emit("refresh"));

const options = computed<WeekOption[]>(() =>
  weeks.value.map((monday) => ({
    value: monday.format("YYYY-MM-DD"),
    label: `wk ${monday.isoWeek()} (${monday.format("D MMM")})`,
  })),
);

const selected = computed(() =>
  options.value.find((option) => option.value === week.value.format("YYYY-MM-DD")),
);

const labelOf = (option: unknown) => (option as WeekOption | undefined)?.label ?? "";

const query = ref("");

const filtered = computed(() =>
  query.value === ""
    ? options.value
    : options.value.filter((option) =>
        option.label.toLowerCase().includes(query.value.toLowerCase()),
      ),
);

const onQuery = (event: Event) => {
  query.value = (event.target as HTMLInputElement).value;
};

const onSelect = (option: WeekOption | null) => {
  query.value = "";

  if (option) {
    state.selectWeek(date(option.value));
  }
};
</script>

<template>
  <div class="px-4 flex flex-row items-center gap-x-2">
    <span class="isolate inline-flex rounded-md shadow-xs">
      <button
        class="relative -mr-px inline-flex items-center rounded-l-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-gray-300 ring-inset hover:bg-gray-50 focus:z-10"
        title="Vorige week"
        type="button"
        @click="state.previousWeek()"
      >
        <ChevronLeftIcon class="size-3"></ChevronLeftIcon>
      </button>

      <button
        class="relative -mr-px inline-flex items-center bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-gray-300 ring-inset hover:bg-gray-50 focus:z-10"
        title="Naar de huidige week"
        type="button"
        @click="state.currentWeek()"
      >
        Deze week
      </button>

      <button
        class="relative inline-flex items-center rounded-r-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-gray-300 ring-inset hover:bg-gray-50 focus:z-10"
        title="Volgende week"
        type="button"
        @click="state.nextWeek()"
      >
        <ChevronRightIcon class="size-3"></ChevronRightIcon>
      </button>
    </span>

    <Combobox as="div" :model-value="selected" @update:model-value="onSelect">
      <ComboboxLabel class="sr-only">Week</ComboboxLabel>

      <div class="relative">
        <ComboboxInput
          :display-value="labelOf"
          class="block w-40 rounded-md bg-white py-2 pr-9 pl-3 text-sm font-semibold text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
          title="Kies een week"
          @input="onQuery"
          @blur="query = ''"
        />

        <ComboboxButton
          class="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-hidden"
        >
          <ChevronDownIcon class="size-5 text-gray-400" aria-hidden="true" />
        </ComboboxButton>

        <transition
          leave-active-class="transition ease-in duration-100"
          leave-from-class=""
          leave-to-class="opacity-0"
        >
          <ComboboxOptions
            v-if="filtered.length > 0 || query.length > 0"
            class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg outline outline-black/5"
          >
            <ComboboxOption
              v-for="option in filtered"
              :key="option.value"
              v-slot="{ active }"
              :value="option"
              as="template"
            >
              <li
                :class="[
                  'relative cursor-default px-3 py-2 select-none',
                  active ? 'bg-indigo-600 text-white outline-hidden' : 'text-gray-900',
                ]"
              >
                <span class="block truncate">{{ option.label }}</span>
              </li>
            </ComboboxOption>
          </ComboboxOptions>
        </transition>
      </div>
    </Combobox>
  </div>
</template>

<style scoped></style>
